import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  makeStyles,
} from "@fluentui/react-components";
import InfiniteScroll from "react-infinite-scroll-component";
import CommandBar from "./CommandBar";
import { apiUrl } from "../utils/api";

const useStyles = makeStyles({
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

const PAGE_SIZE = 30;

type TeamMember = { id?: number; name?: string | null } | null;

type BackendContract = {
  id: number;
  contractNbr: string | null;
  branch: string | null;
  isCollectiveContract?: boolean | null;
  paymentFrequency: number | string | null;
  premiumAnnualGross: number | null;
  annualFee: number | null;
  contact?: {
    id?: number;
    displayName?: string | null;
    accountManagerBO?: TeamMember;
    accountManagerFO?: TeamMember;
  } | null;
  riskObjects?: Array<{ id: number; name?: string | null; riskType?: string | null }>;
};

type Vertrag = {
  id: number;
  Vertragsnummer: string;
  Vertragsart: string;
  Sparte: string;
  Risikoobjekt: string;
  Jahrespraemie: string;
  Zahlweise: string;
  Kunde: string;
  KVerantwortlicher: string;
};

const formatEuro = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
};

const riskObjectLabel = (
  ros?: Array<{ id: number; name?: string | null; riskType?: string | null }>,
): string => {
  if (!ros || ros.length === 0) return "—";
  const names = ros
    .map((r) => (r.name && r.name.trim()) || (r.riskType && r.riskType.trim()) || `#${r.id}`)
    .filter(Boolean);
  return names.join(", ");
};

const teamLabel = (
  bo?: TeamMember,
  fo?: TeamMember,
): string => {
  const parts: string[] = [];
  if (bo?.name) parts.push(`BO: ${bo.name}`);
  if (fo?.name) parts.push(`FO: ${fo.name}`);
  return parts.join(" · ");
};

const mapContract = (c: BackendContract): Vertrag => ({
  id: c.id,
  Vertragsnummer: c.contractNbr ?? "",
  Vertragsart: c.isCollectiveContract ? "Sammelvertrag" : "Einzelvertrag",
  Sparte: c.branch ?? "",
  Risikoobjekt: riskObjectLabel(c.riskObjects),
  Jahrespraemie: formatEuro(c.premiumAnnualGross ?? c.annualFee ?? null),
  Zahlweise: c.paymentFrequency != null ? String(c.paymentFrequency) : "",
  Kunde: c.contact?.displayName ?? "",
  KVerantwortlicher: teamLabel(c.contact?.accountManagerBO, c.contact?.accountManagerFO),
});

const ContractTable: React.FC = () => {
  const classes = useStyles();
  const [vertrag, setVertrag] = useState<Vertrag[]>([]);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await fetch(apiUrl("contracts", { limit: PAGE_SIZE, offset: 0 }));
        if (!res.ok) {
          console.error("Failed to load contracts", res.status, res.statusText);
          return;
        }
        const data = (await res.json()) as BackendContract[];
        const mapped = data.map(mapContract);
        setVertrag(mapped);
        setOffset(mapped.length);
        setHasMore(mapped.length === PAGE_SIZE);
      } catch (error) {
        console.error("Error fetching contracts", error);
      }
    };

    fetchContracts();
  }, []);

  const columns: TableColumnDefinition<Vertrag>[] = React.useMemo(
    () => [
      createTableColumn<Vertrag>({
        columnId: "Vertragsnummer",
        renderHeaderCell: () => <>Vertragsnummer</>,
        renderCell: (item) => <>{item.Vertragsnummer}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Vertragsart",
        renderHeaderCell: () => <>Vertragsart</>,
        renderCell: (item) => <>{item.Vertragsart}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Sparte",
        renderHeaderCell: () => <>Sparte</>,
        renderCell: (item) => <>{item.Sparte}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Risikoobjekt",
        renderHeaderCell: () => <>Risikoobjekt</>,
        renderCell: (item) => <>{item.Risikoobjekt}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Jahrespraemie",
        renderHeaderCell: () => <>Jahresprämie</>,
        renderCell: (item) => <>{item.Jahrespraemie}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Zahlweise",
        renderHeaderCell: () => <>Zahlweise</>,
        renderCell: (item) => <>{item.Zahlweise}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Kunde",
        renderHeaderCell: () => <>Kunde</>,
        renderCell: (item) => <>{item.Kunde}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "KVerantwortlicher",
        renderHeaderCell: () => <>K-Verantwortlicher</>,
        renderCell: (item) => <>{item.KVerantwortlicher}</>,
      }),
    ],
    [],
  );

  const columnSizingOptions: TableColumnSizingOptions = React.useMemo(
    () => ({
      Vertragsnummer: { defaultWidth: 195, minWidth: 100 },
      Vertragsart: { defaultWidth: 140, minWidth: 110 },
      Sparte: { defaultWidth: 160, minWidth: 100 },
      Risikoobjekt: { defaultWidth: 260, minWidth: 140 },
      Jahrespraemie: { defaultWidth: 130, minWidth: 100 },
      Zahlweise: { defaultWidth: 110, minWidth: 80 },
      Kunde: { defaultWidth: 240, minWidth: 140 },
      KVerantwortlicher: { defaultWidth: 200, minWidth: 120 },
    }),
    [],
  );

  const filteredContracts = vertrag.filter((k) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      k.Vertragsnummer.toLowerCase().includes(q) ||
      k.Sparte.toLowerCase().includes(q) ||
      k.Risikoobjekt.toLowerCase().includes(q) ||
      k.Kunde.toLowerCase().includes(q)
    );
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const { getRows, tableRef, columnSizing_unstable } = useTableFeatures(
    { columns, items: filteredContracts },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );

  const navigate = useNavigate();
  // Klick auf eine Zeile öffnet die Vertragsdetail-View.
  const handleEdit = (item: Vertrag) => {
    if (item?.id) navigate(`/contracts/${item.id}`);
  };

  const rows = getRows();

  const fetchMoreContracts = async () => {
    try {
      const res = await fetch(apiUrl("contracts", { limit: PAGE_SIZE, offset }));
      if (!res.ok) {
        console.error("Failed to load more contracts", res.status, res.statusText);
        setHasMore(false);
        return;
      }
      const data = (await res.json()) as BackendContract[];
      const mapped = data.map(mapContract);
      setVertrag((prev) => [...prev, ...mapped]);
      setOffset((prev) => prev + mapped.length);
      if (mapped.length < PAGE_SIZE) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more contracts", error);
      setHasMore(false);
    }
  };

  return (
    <div style={{ fontSize: "14px", width: "100vw", overflowX: "auto" }}>
      <CommandBar
        header="Verträge"
        onSearchChange={handleSearchChange}
        canCreate={false}
        canDelete={false}
      />
      <div
        id="scrollableDivContracts"
        style={{ height: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        <InfiniteScroll
          dataLength={filteredContracts.length}
          next={fetchMoreContracts}
          hasMore={hasMore}
          loader={
            <div style={{ textAlign: "center" }}>Lade weitere Verträge...</div>
          }
          scrollableTarget="scrollableDivContracts"
          style={{ overflow: "visible" }}
        >
          <Table
            ref={tableRef}
            aria-label="Vertrags-Tabelle"
            {...columnSizing_unstable.getTableProps()}
          >
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHeaderCell
                    key={col.columnId}
                    {...columnSizing_unstable.getTableHeaderCellProps(col.columnId)}
                  >
                    {col.renderHeaderCell()}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ item }, i) => (
                <TableRow
                  key={item.id}
                  className={i % 2 === 1 ? classes.stripedRow : undefined}
                  onClick={() => handleEdit(item)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Vertragsnummer")}
                  >
                    <div
                      className={classes.cellEllipsis}
                      title={item.Vertragsnummer}
                    >
                      {item.Vertragsnummer}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Vertragsart")}
                  >
                    <div className={classes.cellEllipsis} title={item.Vertragsart}>
                      {item.Vertragsart}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Sparte")}
                  >
                    <div className={classes.cellEllipsis} title={item.Sparte}>
                      {item.Sparte}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Risikoobjekt")}
                  >
                    <div className={classes.cellEllipsis} title={item.Risikoobjekt}>
                      {item.Risikoobjekt}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Jahrespraemie")}
                  >
                    <div
                      className={classes.cellEllipsis}
                      title={item.Jahrespraemie}
                    >
                      {item.Jahrespraemie}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Zahlweise")}
                  >
                    <div className={classes.cellEllipsis} title={item.Zahlweise}>
                      {item.Zahlweise}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Kunde")}
                  >
                    <div className={classes.cellEllipsis} title={item.Kunde}>
                      {item.Kunde}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("KVerantwortlicher")}
                  >
                    <div
                      className={classes.cellEllipsis}
                      title={item.KVerantwortlicher}
                    >
                      {item.KVerantwortlicher || "—"}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ContractTable;
